import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { WizardStep } from "@shared/components/application/navigation/Wizard/WizardStep.web";
import { useWizardContext } from "@shared/contexts/WizardContext/WizardContext";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { useCallback } from "react";
import CheckoutLogin from "@app-contexts/public/shared/checkout/components/steps/login/CheckoutLogin/CheckoutLogin.web";
import CheckoutBaseLayoutWithCart from "@app-contexts/public/shared/checkout/templates/CheckoutBaseLayoutWithCart/CheckoutBaseLayoutWithCart.web";
import CheckoutSummary from "@app-contexts/public/shared/checkout/components/CheckoutSummary/CheckoutSummary.web";
import { useTranslation } from "@shared/utils/internationalization";

export const LOGIN_STEP_ID = "login";

type LoginStepProps = {
  forceGuestCheckout?: boolean;
  hideNextButton?: boolean;
};

export function LoginStep({
  forceGuestCheckout = false,
  hideNextButton = false,
}: LoginStepProps) {
  const { isLoggedIn } = useUserSessionAuthState();
  const { nextStep } = useWizardContext();
  const publicBasket = usePublicBasket();
  const { t } = useTranslation("checkout");
  const { paymentVenue, isGroupSale } = publicBasket;

  const { useRetrieve: useRetrieveVenue } = usePublicVenueService();
  const { data: venue } = useRetrieveVenue(paymentVenue);

  return (
    <WizardStep
      stepId={LOGIN_STEP_ID}
      stepName={t("Log in", { ns: "common" })}
      async
      customData={{
        hideNextButton,
      }}
      isReady={
        isLoggedIn !== undefined &&
        Boolean(paymentVenue) === Boolean(venue !== undefined)
      } // need to wait for venue for user interaction tracking to be accurate
      shouldRegister={isLoggedIn === false && !isGroupSale}
    >
      {({ onValid }) => {
        const handleLoginSuccess = useCallback(
          (isGuestCheckout: boolean) => {
            onValid?.();
            nextStep();
            UserInteractionLogger.initiateCheckout({
              additionalParams: {
                step: "login_completed",
                type: isGuestCheckout ? "guest" : "account",
              },
              basket: publicBasket,
            });
          },
          [onValid, nextStep]
        );

        return (
          <CheckoutBaseLayoutWithCart
            rightColumn={
              <CheckoutSummary allowApplyingDiscounts={isLoggedIn} />
            }
          >
            <CheckoutLogin
              onAuthSuccess={handleLoginSuccess}
              forceGuestCheckout={forceGuestCheckout}
            />
          </CheckoutBaseLayoutWithCart>
        );
      }}
    </WizardStep>
  );
}

LoginStep.id = LOGIN_STEP_ID;
